import { MatrixEventEvent } from 'matrix-js-sdk';
import { updateEvent } from '../../reducer/matrix';
import { createTEvent } from './event';
// Track which MatrixEvent instances already have a Decrypted listener
// attached so we never double-attach (would cause duplicate Redux dispatches
// on every successful retry decryption).
const ATTACHED = new WeakSet();
const isEncryptedTimelineEvent = (evt) => (typeof evt.isEncrypted === 'function' && evt.isEncrypted()) ||
    evt.getType() === 'm.room.encrypted';
const isDecryptionFailure = (evt) => {
    if (typeof evt.isDecryptionFailure === 'function' && evt.isDecryptionFailure()) {
        return true;
    }
    return evt.getContent?.()?.msgtype === 'm.bad.encrypted';
};
const mirrorDecryptedEventToRedux = async (evt, roomId, dispatch) => {
    if (isDecryptionFailure(evt))
        return;
    const te = await createTEvent(evt);
    if (te && te.content?.msgtype !== 'm.bad.encrypted') {
        dispatch(updateEvent({ event: te, roomId }));
    }
};
/**
 * Attach a `MatrixEventEvent.Decrypted` listener to `evt` that mirrors the
 * decrypted payload back into Redux via `updateEvent`. The listener stays
 * attached across all retry attempts (failures included) and removes itself
 * only after the event has been successfully decrypted, so we keep catching
 * the late retry that fires once the megolm room key finally arrives.
 *
 * Idempotent: a no-op if `evt` already has a listener attached, or if `evt`
 * is not encrypted to begin with.
 */
export const attachDecryptedListener = (evt, roomId, dispatch) => {
    if (!evt)
        return;
    if (ATTACHED.has(evt))
        return;
    if (!isEncryptedTimelineEvent(evt))
        return;
    ATTACHED.add(evt);
    const handler = async (decryptedEvent) => {
        try {
            await mirrorDecryptedEventToRedux(decryptedEvent, roomId, dispatch);
        }
        finally {
            // Detach only after a successful decryption so the listener keeps
            // catching the auto-retry the rust-crypto SDK fires when a missing
            // megolm room key arrives later via to-device.
            if (!decryptedEvent.isDecryptionFailure()) {
                decryptedEvent.removeListener(MatrixEventEvent.Decrypted, handler);
                ATTACHED.delete(decryptedEvent);
            }
        }
    };
    evt.on(MatrixEventEvent.Decrypted, handler);
};
/**
 * Attach Decrypted listeners and push any cleartext the SDK already holds into
 * Redux. Also retries decrypt for events that still fail but may have keys now.
 */
export const syncDecryptedTimelineToRedux = (rooms, client, dispatch) => {
    for (const room of rooms) {
        for (const evt of room.getLiveTimeline().getEvents()) {
            if (!isEncryptedTimelineEvent(evt))
                continue;
            attachDecryptedListener(evt, room.roomId, dispatch);
            if (!isDecryptionFailure(evt)) {
                void mirrorDecryptedEventToRedux(evt, room.roomId, dispatch);
                continue;
            }
            void client
                .decryptEventIfNeeded(evt, { isRetry: true })
                .then(() => mirrorDecryptedEventToRedux(evt, room.roomId, dispatch))
                .catch(() => undefined);
        }
    }
};
/** @deprecated Use syncDecryptedTimelineToRedux */
export const backfillDecryptedListeners = (rooms, dispatch) => {
    for (const room of rooms) {
        for (const evt of room.getLiveTimeline().getEvents()) {
            attachDecryptedListener(evt, room.roomId, dispatch);
        }
    }
};
